import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { GroupCard } from "../UserGroupCard.component";
import { ChevronDown, ChevronUp, Download, Plus } from "lucide-react";
import { SectionHeader, searchBoxSx } from "../../components/detailLayout";
import { GroupCardData } from "../types";
import { useTranslation } from "react-i18next";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { useMemo, useState } from "react";
import { ServicesURL } from "@utils/utilsApi";
import { useNavigate, useParams } from "react-router-dom";
import AddToStaticGroupsModal from "../../components/AddToStaticGroupsModal";
import { useClientPaginatedList } from "@hooks/useClientPaginatedList";
import PaginatorCst from "@components/PaginatorCst";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { SearchInput } from "src/UI/SearchInput";
import { SelectInput } from "src/UI/SelectInput";
import { ButtonTertiary } from "src/UI/ButtonTertiary";
import { useAuth } from "@context/auth";
import { openToast } from "@utils/toastStore";
import { updateUserStaticGroups } from "../../updateUserStaticGroups";

const formatDate = (value: any): string => {
  if (value === null || value === undefined || value === "") return "";
  const date = new Date(typeof value === "number" ? value : String(value));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("it-IT");
};

const PAGE_SIZE = 5;

export const GroupsSection = ({ user }: { user: any }) => {
  const theme = useTheme();
  const { t } = useTranslation("user-detail-v2");
  const navigate = useNavigate();
  const { id: corporateId = "", userId = "" } = useParams();
  const fullName = [user?.name, user?.surname].filter(Boolean).join(" ").trim();
  const { user: loggedUser } = useAuth();
  const canDeleteGroupEnrollment = !!loggedUser?.permissions?.includes(
    "usermgmt.corporates.static_groups.write",
  );

  const [addToGroupsOpen, setAddToGroupsOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<GroupCardData | null>(null);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);
  const [groupsPage, setGroupsPage] = useState(1);
  const [groupsPageSize, setGroupsPageSize] = useState(PAGE_SIZE);
  const [groupsSearch, setGroupsSearch] = useState("");
  const [groupsTypeFilter, setGroupsTypeFilter] = useState("");
  const [groupsSortOrder, setGroupsSortOrder] = useState<"desc" | "asc">(
    "desc",
  );
  const {
    dataDetail: userGroupsData,
    mutate: mutateUserGroups,
    isLoading,
  } = useFetchDataDetail(
    corporateId && userId
      ? `${ServicesURL.corporate}/${corporateId}/users/${userId}/groups`
      : null,
  );

  const allGroups: GroupCardData[] = useMemo(() => {
    const list = Array.isArray(userGroupsData)
      ? userGroupsData
      : //@ts-ignore
        userGroupsData?.groups_list || [];
    return list.map((g: any) => ({
      id: g.id,
      name: g.name || "-",
      type: g.group_type,
      createdAt: g.creation_date || g.created_date || g.createdAt,
    }));
  }, [userGroupsData]);

  const typeFilteredGroups = useMemo(() => {
    if (!groupsTypeFilter) return allGroups;
    return allGroups.filter((g) => g.type === groupsTypeFilter);
  }, [allGroups, groupsTypeFilter]);

  const sortedGroups = useMemo(() => {
    const list = [...typeFilteredGroups];
    list.sort((a, b) => {
      const aTime = new Date(a.createdAt ?? 0).getTime();
      const bTime = new Date(b.createdAt ?? 0).getTime();
      return groupsSortOrder === "desc" ? bTime - aTime : aTime - bTime;
    });
    return list;
  }, [typeFilteredGroups, groupsSortOrder]);

  const toggleGroupsSortOrder = () => {
    setGroupsSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    setGroupsPage(1);
  };

  const {
    items: groups,
    totalItems: groupsTotal,
    totalPages: groupsPages,
  } = useClientPaginatedList(
    sortedGroups,
    {
      page: groupsPage,
      pageSize: groupsPageSize,
      search: groupsSearch || undefined,
    },
    {
      searchPredicate: (g, search) =>
        g.name.toLowerCase().includes(search.toLowerCase()),
    },
  );

  const goToGroupDetail = (g: GroupCardData) => {
    const segment = g.type === "DYNAMIC" ? "dynamic-groups" : "groups";
    navigate(
      `/v2/corporate/${corporateId}/detail/users-groups/${segment}/${g.id}`,
    );
  };

  const handleDownloadGroupsCsv = () => {
    const escape = (value: any) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;
    const header = [
      t("groups.csv.id"),
      t("groups.csv.name"),
      t("groups.csv.type"),
      t("groups.csv.createdAt"),
    ];
    const rows = allGroups.map((g) =>
      [
        g.id,
        g.name,
        g.type === "DYNAMIC" ? t("groups.typeDynamic") : t("groups.typeStatic"),
        formatDate(g.createdAt),
      ]
        .map(escape)
        .join(","),
    );
    const bom = "\uFEFF";
    const sepDirective = "sep=,\r\n";
    const csvContent = `${bom}${sepDirective}${header
      .map(escape)
      .join(",")}\r\n${rows.join("\r\n")}\r\n`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `user-${userId}-groups.csv`;
    link.click();
    window.URL.revokeObjectURL(link.href);
  };

  const confirmDeleteGroupEnrollment = async () => {
    if (!deleteTarget || !userId) return;
    setIsDeletingGroup(true);
    try {
      await updateUserStaticGroups(userId, {
        addGroupIds: [],
        removeGroupIds: [Number(deleteTarget.id)],
      });
      setDeleteTarget(null);
      mutateUserGroups && mutateUserGroups();
      openToast({
        severity: "success",
        message: String(t("groups.deleteConfirm.success")),
      });
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("groups.deleteConfirm.error")),
      });
    } finally {
      setIsDeletingGroup(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <SectionHeader
        sx={{ mb: 1 }}
        title={t("groups.title")}
        action={
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <ButtonPrimary
              endIcon={<Plus size={16} />}
              onClick={() => setAddToGroupsOpen(true)}
              // P29: come v1, i membri dei gruppi statici si gestiscono con
              // static_groups.write (stesso permesso della rimozione).
              disabled={!canDeleteGroupEnrollment}
            >
              {t("groups.addToStatic")}
            </ButtonPrimary>
            <ButtonSecondary
              endIcon={<Download size={16} />}
              onClick={handleDownloadGroupsCsv}
              disabled={allGroups.length === 0}
            >
              {t("downloadCsv")}
            </ButtonSecondary>
          </Box>
        }
      />
      <Stack
        flexDirection={"row"}
        gap={"1rem"}
        flexWrap={"wrap"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Stack
          flexDirection={"row"}
          gap={"1rem"}
          flexWrap={"wrap"}
          alignItems={"center"}
          sx={{ flex: 1 }}
        >
          <Box sx={searchBoxSx}>
            <SearchInput
              id="groups-search"
              value={groupsSearch}
              placeholder={t("search")}
              onChange={(e: any) => {
                setGroupsSearch(e.target.value);
                setGroupsPage(1);
              }}
              sx={{ width: "100%" }}
            />
          </Box>
          <SelectInput
            id="groups-typology-filter"
            value={groupsTypeFilter}
            onChange={(e) => {
              setGroupsTypeFilter(e.target.value);
              setGroupsPage(1);
            }}
            options={[
              { value: "", label: t("groups.typology") },
              { value: "STATIC", label: t("groups.typeStatic") },
              { value: "DYNAMIC", label: t("groups.typeDynamic") },
            ]}
            sx={{
              width: { xs: "100%", sm: "12rem" },
            }}
          />

          {/* TODO Filtro per status: attributo non ancora presente sul dato gruppo, da attivare quando disponibile
        <SelectInput
          id="groups-status-filter"
          value={groupsStatusFilter}
          onChange={(e) => {
            setGroupsStatusFilter(e.target.value);
            setGroupsPage(1);
          }}
          options={[
            { value: "", label: t("groups.statusAll") },
          ]}
          sx={{
            width: { xs: "100%", sm: "12rem" },
          }}
        />
        */}
        </Stack>
        <ButtonTertiary
          endIcon={
            groupsSortOrder === "desc" ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronUp size={16} />
            )
          }
          onClick={toggleGroupsSortOrder}
          sx={{
            alignSelf: "end",
          }}
        >
          {groupsSortOrder === "desc"
            ? t("groups.sortNewest")
            : t("groups.sortOldest")}
        </ButtonTertiary>
      </Stack>

      {isLoading ? (
        <CircularProgress />
      ) : groups.length === 0 ? (
        <Typography sx={{ color: theme.palette.text.tertiary }}>
          {t("groups.empty")}
        </Typography>
      ) : (
        groups.map((g) => {
          return (
            <GroupCard
              key={g.id}
              group={g}
              onDetail={() => goToGroupDetail(g)}
              onDeleteEnrollment={() => setDeleteTarget(g)}
              canDeleteEnrollment={canDeleteGroupEnrollment}
            />
          );
        })
      )}

      {groupsPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PaginatorCst
            page={groupsPage}
            pageSize={groupsPageSize}
            totalItems={groupsTotal}
            onPageChange={setGroupsPage}
            onPageSizeChange={(next) => {
              setGroupsPageSize(next);
              setGroupsPage(1);
            }}
          />
        </Box>
      )}

      <AddToStaticGroupsModal
        open={addToGroupsOpen}
        onClose={() => setAddToGroupsOpen(false)}
        corporateId={corporateId}
        userId={userId}
        userName={fullName || user?.email || "-"}
        onSuccess={() => mutateUserGroups && mutateUserGroups()}
      />

      <ConfirmDialogCst
        open={!!deleteTarget}
        title={String(t("groups.deleteConfirm.title"))}
        description={String(
          t("groups.deleteConfirm.description", {
            name: deleteTarget?.name ?? "",
          }),
        )}
        confirmLabel={String(t("groups.deleteConfirm.confirm"))}
        cancelLabel={String(t("groups.deleteConfirm.cancel"))}
        confirmVariant="danger"
        isSubmitting={isDeletingGroup}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteGroupEnrollment}
      />
    </Box>
  );
};
